import {openQuestionModal} from '../../../connected-components/questionModal/actions';
import {modalTypes} from '../../enums';
import {MediaFile} from '../../../interfaces/connected-components/uploadModal';
import {CameraOptions, GalleryOptions} from './types';

/**
 * Helper to convert file to Base64
 */
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

/**
 * Web implementation of the image gallery picker
 * Uses the HTML file input to allow users to select images/videos
 */
export const galleryLaunch = async (
  options?: GalleryOptions,
): Promise<MediaFile | undefined> => {
  try {
    // Create a file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.style.display = 'none';
    
    // Set accept attribute based on mediaType
    const mediaType = options?.mediaType || 'mixed';
    if (mediaType === 'photo') {
      fileInput.accept = 'image/*';
    } else if (mediaType === 'video') {
      fileInput.accept = 'video/*';
    } else {
      fileInput.accept = 'image/*,video/*';
    }
    
    // Set multiple attribute if selectionLimit > 1
    if (options?.selectionLimit && options.selectionLimit > 1) {
      fileInput.multiple = true;
    }
    
    document.body.appendChild(fileInput);
    
    // Create a promise that resolves when a file is selected
    const filePromise = new Promise<MediaFile | undefined>(resolve => {
      fileInput.addEventListener('change', async () => {
        if (fileInput.files && fileInput.files.length > 0) {
          const file = fileInput.files[0];
          
          // Create a URL for the selected file
          const uri = URL.createObjectURL(file);
          
          // Extract file name and type
          const name = file.name;
          const type = file.type;
          
          // Include base64 data if requested
          let base64 = undefined;
          if (options?.includeBase64 === true) {
            try {
              base64 = await fileToBase64(file);
            } catch (error) {
            }
          }
          
          // Clean up by removing the file input
          document.body.removeChild(fileInput);
          
          // Resolve with the media file information
          resolve({
            name,
            uri,
            type,
            base64,
          });
        } else {
          // Clean up if no file was selected
          document.body.removeChild(fileInput);
          resolve(undefined);
        }
      });
      
      // Handle cancellation
      fileInput.addEventListener('cancel', () => {
        document.body.removeChild(fileInput);
        resolve(undefined);
      });
    });
    
    // Trigger the file input dialog
    fileInput.click();
    
    // Return the promise result
    return await filePromise;
  } catch (error) {
    return undefined;
  }
};

/**
 * Web implementation of the camera launcher
 * Uses the MediaDevices API to access the webcam
 */
export const cameraLaunch = async (
  options?: CameraOptions,
): Promise<MediaFile | undefined> => {
  try {
    // Check if the browser supports the MediaDevices API
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      openQuestionModal({
        message: 'Your browser does not support camera access.',
      });
      return undefined;
    }
    
    // Create a modal container
    const modalContainer = document.createElement('div');
    modalContainer.style.position = 'fixed';
    modalContainer.style.top = '0';
    modalContainer.style.left = '0';
    modalContainer.style.width = '100%';
    modalContainer.style.height = '100%';
    modalContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    modalContainer.style.zIndex = '10000';
    modalContainer.style.display = 'flex';
    modalContainer.style.flexDirection = 'column';
    modalContainer.style.alignItems = 'center';
    modalContainer.style.justifyContent = 'center';
    
    // Create video element for the camera feed
    const video = document.createElement('video');
    video.style.maxWidth = '100%';
    video.style.maxHeight = '80%';
    video.autoplay = true;
    
    // Create buttons container
    const buttonsContainer = document.createElement('div');
    buttonsContainer.style.marginTop = '20px';
    buttonsContainer.style.display = 'flex';
    buttonsContainer.style.gap = '10px';
    
    // Create capture button
    const captureButton = document.createElement('button');
    captureButton.textContent = 'Capture';
    captureButton.style.padding = '10px 20px';
    captureButton.style.backgroundColor = '#007AFF';
    captureButton.style.color = 'white';
    captureButton.style.border = 'none';
    captureButton.style.borderRadius = '5px';
    captureButton.style.cursor = 'pointer';
    
    // Create cancel button
    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.style.padding = '10px 20px';
    cancelButton.style.backgroundColor = '#FF3B30';
    cancelButton.style.color = 'white';
    cancelButton.style.border = 'none';
    cancelButton.style.borderRadius = '5px';
    cancelButton.style.cursor = 'pointer';
    
    // Add elements to the DOM
    buttonsContainer.appendChild(captureButton);
    buttonsContainer.appendChild(cancelButton);
    modalContainer.appendChild(video);
    modalContainer.appendChild(buttonsContainer);
    document.body.appendChild(modalContainer);
    
    // Request camera access
    const stream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: false,
    });
    
    // Attach the stream to the video element
    video.srcObject = stream;
    
    // Create a promise that resolves when a photo is captured or canceled
    const capturePromise = new Promise<MediaFile | undefined>(resolve => {
      // Handle capture button click
      captureButton.addEventListener('click', () => {
        // Create a canvas to capture the current video frame
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Get the quality setting (0-1)
        const quality = typeof options?.quality === 'number' ? 
          Math.max(0, Math.min(1, options.quality)) : 0.8;
        
        // Convert the canvas to a data URL - this is always needed for the URI
        const imageType = 'image/jpeg';
        const dataUrl = canvas.toDataURL(imageType, quality);
        
        // Clean up
        stopStreamAndRemoveModal(stream, modalContainer);
        
        // Generate a filename with timestamp
        const timestamp = new Date().toISOString();
        const name = `photo_${timestamp}.jpg`;
        
        // Include base64 data if requested - note that dataUrl already is a base64 string
        // but we should only include it if specifically requested
        const base64 = options?.includeBase64 === true ? dataUrl : undefined;
        
        // Resolve with the media file information
        resolve({
          name,
          uri: dataUrl,
          type: imageType,
          base64,
        });
      });
      
      // Handle cancel button click
      cancelButton.addEventListener('click', () => {
        stopStreamAndRemoveModal(stream, modalContainer);
        resolve(undefined);
      });
    });
    
    // Return the promise result
    return await capturePromise;
  } catch (error) {
    
    // Check if the error is a permission error
    if (error instanceof DOMException && error.name === 'NotAllowedError') {
      openQuestionModal({
        title: 'Permission Denied',
        message: 
          'Camera access was denied. Please allow camera access in your browser settings.',
        type: modalTypes.question,
        confirmText: 'OK',
      });
    } else {
      openQuestionModal({
        message: 'An error occurred while trying to access the camera.',
      });
    }
    
    return undefined;
  }
};

/**
 * Helper function to stop the media stream and remove the modal
 */
const stopStreamAndRemoveModal = (
  stream: MediaStream,
  modalContainer: HTMLDivElement,
): void => {
  // Stop all tracks in the stream
  stream.getTracks().forEach(track => track.stop());
  
  // Remove the modal from the DOM
  if (modalContainer.parentNode) {
    modalContainer.parentNode.removeChild(modalContainer);
  }
}; 